import React, { memo, useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Select } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { isMobile } from 'utils/browser';
import { isEmpty } from 'utils/isEmpty';
import { getEntityValue } from 'services/Fuzzy';
import { getEntityCustomization } from 'actions/Fuzzy';

function isEqual(innerValue, externalValue, isMulti) {
    if (isMulti) {
        if (!Array.isArray(innerValue) || !Array.isArray(externalValue)) return false;
        if (innerValue.length !== externalValue.length) return false;
        return innerValue.every((v, idx) => String(externalValue[idx]) === String(v?.value));
    }
    return innerValue?.value === externalValue;
}

// Ensures value has a valid label, using fallback if empty
function ensureLabel(val) {
    if (!val || typeof val !== 'object') return val;
    if (val.label === '' || val.label === undefined || val.label === null) {
        return { ...val, label: getLiteral('label_no_data') };
    }
    return val;
}

const SelectHoi = memo(
    ({
        usePlainValue,
        options,
        value,
        onChange,
        loadOptions,
        list,
        preferOptionsForValue,
        isMulti,
        placeholder,
        placeholderReadOnly,
        autoFocus,
        ...props
    }) => {
        const [innerValue, setInnerValue] = useState(null);
        const valueRef = useRef(null);

        // Fallback: find value in options (from loadOptions or options prop)
        const findValueInOptions = useCallback(
            (targetValue) => {
                const valuesToFind = isMulti
                    ? Array.isArray(targetValue)
                        ? targetValue
                        : targetValue != null
                          ? [targetValue]
                          : []
                    : [targetValue];

                const findInList = (optionsList) => {
                    if (!optionsList?.length) return null;

                    // Flatten grouped options
                    const flatOptions = optionsList.flatMap((opt) =>
                        opt.options ? opt.options : [opt],
                    );

                    const foundOptions = valuesToFind
                        .map((v) => flatOptions.find((opt) => String(opt.value) === String(v)))
                        .filter(Boolean);

                    if (!foundOptions.length) return null;

                    return isMulti ? foundOptions : foundOptions[0];
                };

                // If options provided, use them directly
                if (options?.length) {
                    return Promise.resolve(findInList(options));
                }

                // Otherwise try to load them
                if (loadOptions) {
                    return loadOptions()
                        .then(findInList)
                        .catch(() => null);
                }

                return Promise.resolve(null);
            },
            [isMulti, loadOptions, options],
        );

        useEffect(() => {
            if (!usePlainValue) return;
            if (isEqual(valueRef.current, value, isMulti)) return;
            if (isEmpty(value)) {
                valueRef.current = null;
                setInnerValue();
            } else if (preferOptionsForValue) {
                findValueInOptions(value).then((foundOption) => {
                    valueRef.current = foundOption || null;
                    setInnerValue(foundOption || undefined);
                });
            } else if (list) {
                getEntityValue(list, isMulti ? value : [value])
                    .then((result) => {
                        if (!result?.data) {
                            // No data returned, fallback to loadOptions
                            return findValueInOptions(value).then((fallbackValue) => {
                                if (fallbackValue) {
                                    valueRef.current = fallbackValue;
                                    setInnerValue(fallbackValue);
                                }
                            });
                        }
                        const finalValue = isMulti
                            ? value?.map((v) => ({
                                  label: result?.data?.[v]?.description,
                                  value: String(result?.data?.[v]?.id),
                                  ...getEntityCustomization(result?.data?.[v], list),
                              }))
                            : {
                                  label: result?.data?.[value]?.description,
                                  value: String(result?.data?.[value]?.id),
                                  ...getEntityCustomization(result?.data?.[value], list),
                              };
                        valueRef.current = finalValue;
                        setInnerValue(finalValue);
                    })
                    .catch(() => {
                        // Backend error, fallback to loadOptions
                        findValueInOptions(value).then((fallbackValue) => {
                            if (fallbackValue) {
                                valueRef.current = fallbackValue;
                                setInnerValue(fallbackValue);
                            }
                        });
                    });
            } else if (options?.length) {
                findValueInOptions(value).then((foundOption) => {
                    valueRef.current = foundOption || null;
                    setInnerValue(foundOption || undefined);
                });
            }
        }, [
            findValueInOptions,
            isMulti,
            list,
            options,
            preferOptionsForValue,
            usePlainValue,
            value,
        ]);

        const onChangeValue = useCallback(
            (value) => {
                valueRef.current = value;
                setInnerValue(value);
                onChange(
                    usePlainValue
                        ? isMulti
                            ? value?.map((v) => v.value) || []
                            : value?.value
                        : value,
                    value,
                );
            },
            [isMulti, onChange, usePlainValue],
        );

        const finalPlaceholder = useMemo(() => {
            if (placeholder) return placeholder;
            if (isMulti) return getLiteral('placeholder_select');
            return getLiteral('label_selectone');
        }, [placeholder, isMulti]);

        // Ensure value has valid label (fallback for empty labels)
        const displayValue = useMemo(() => {
            const val = usePlainValue ? innerValue : value;
            if (!val) return val;
            if (isMulti && Array.isArray(val)) {
                return val.map(ensureLabel);
            }
            return ensureLabel(val);
        }, [usePlainValue, innerValue, value, isMulti]);

        return (
            <Select
                isSearchable={!isMobile}
                options={options}
                value={displayValue}
                onChange={onChangeValue}
                loadOptions={loadOptions}
                isMulti={isMulti}
                placeholder={finalPlaceholder}
                placeholderReadOnly={placeholderReadOnly || finalPlaceholder}
                hideSelectedOptions={false}
                forceStartFocused={autoFocus}
                {...props}
            />
        );
    },
);

export default SelectHoi;
